import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchAllProductTags, tagToSlug } from '@/services/shopify';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';

interface ShopCategoryNavProps {
  activeCategory?: string;
}

export const ShopCategoryNav: React.FC<ShopCategoryNavProps> = ({ activeCategory }) => {
  const navigate = useNavigate();
  const [tags, setTags] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadTags = async () => {
      try {
        setIsLoading(true);
        const productTags = await fetchAllProductTags();
        setTags(productTags);
      } catch (error) {
        console.error('Error loading tags:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadTags();
  }, []);

  const handleCategoryClick = (slug?: string) => {
    if (!slug) {
      navigate('/shop');
    } else {
      navigate(`/shop?category=${slug}`);
    }
  };

  if (isLoading) {
    return (
      <div className="sticky top-16 md:top-[72px] z-40 bg-background border-b">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8 py-4 flex justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="sticky top-16 md:top-[72px] z-40 bg-background border-b">
      <div className="max-w-screen-2xl mx-auto px-4 md:px-8">
        <div className="flex gap-4 overflow-x-auto scrollbar-hide py-3 md:py-4 snap-x snap-mandatory">
          {/* All Categories Button */}
          <Button
            variant="ghost"
            onClick={() => handleCategoryClick()}
            className={`whitespace-nowrap text-sm font-chroma transition-colors snap-start ${
              !activeCategory
                ? 'text-foreground border-b-2 border-primary rounded-none pb-3'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            All
          </Button>

          {/* Dynamic Tag Buttons */}
          {tags.map((tag) => {
            const slug = tagToSlug(tag);
            return (
              <Button
                key={slug}
                variant="ghost"
                onClick={() => handleCategoryClick(slug)}
                className={`whitespace-nowrap text-sm font-chroma transition-colors snap-start capitalize ${
                  activeCategory === slug
                    ? 'text-foreground border-b-2 border-primary rounded-none pb-3'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {tag}
              </Button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
